// frontend/user/src/components/file-view/media-area.tsx
// 媒体/文件展示区(从 routes/file-preview.tsx 抽出)。按 mime 渲染:
//   图片 <img> / 视频 <video> / 音频(封面+<audio>) / 非媒体(文件图标占位)。
// 详情页(zkY66/kENKt/qCOtS/pKcdM)与预览页(q5D4P/AGXin/Qk72B/AFxaz)共用;
// frameId 用于音频 title/artist testid。

import { Spinner } from '@dogdisk/shared'

export function MediaArea({
  frameId,
  isImage,
  isVideo,
  isAudio,
  isLoading,
  streamURL,
  coverUrl,
  name,
  mime,
  grow = true,
}: {
  frameId: string
  isImage: boolean
  isVideo: boolean
  isAudio: boolean
  isLoading: boolean
  streamURL: string | null
  coverUrl: string | null
  name: string | null | undefined
  mime: string
  /** true(默认,/preview 页):媒体区 flex-1 撑满视口;false(/file 详情页):媒体区不撑满,内容自上而下。 */
  grow?: boolean
}) {
  const isMedia = isImage || isVideo || isAudio
  const wrapCls = grow ? 'flex-1 px-5' : 'px-5'

  if (isLoading) {
    return (
      <div className={wrapCls}>
        <div className="flex h-80 items-center justify-center">
          <Spinner className="text-gray-400" />
        </div>
      </div>
    )
  }

  // 非媒体:文件图标 + 后缀占位(不内联渲染内容)
  if (!isMedia) {
    return (
      <div className="px-5">
        <div className="my-2 flex h-[220px] items-center justify-center rounded-2xl bg-[#F6F7F8]">
          <div className="flex flex-col items-center gap-2">
            <span className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[#FFE4E4] text-[#FF6B6B]">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" /></svg>
            </span>
            <span className="text-[12px] font-semibold text-[#FF6B6B]">{(mime.split('/').pop() ?? 'FILE').toUpperCase()}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={wrapCls}>
      {!streamURL ? (
        <div className="flex h-80 items-center justify-center px-6 text-center text-[13px] text-[#FF6B6B]">
          无法加载预览(文件元数据缺失)
        </div>
      ) : isImage ? (
        <img src={streamURL} alt={name ?? ''} className="h-auto w-full rounded-2xl object-contain" />
      ) : isVideo ? (
        <video src={streamURL} controls playsInline className="aspect-video w-full rounded-2xl bg-black" />
      ) : (
        /* audio:封面专辑图 + 标题 + 未知艺术家 + 原生 audio controls */
        <div className="flex flex-col items-center">
          {coverUrl ? (
            <img src={coverUrl} alt="封面" className="aspect-square w-48 rounded-2xl object-cover shadow-md" />
          ) : (
            <div className="flex aspect-square w-48 items-center justify-center rounded-2xl bg-gradient-to-br from-[#19caad] to-[#1d9be0] text-white shadow-md">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
            </div>
          )}
          <p data-testid={`${frameId}-title`} className="mt-4 text-center text-[18px] font-semibold text-[#1A1A1A]">{name}</p>
          <p data-testid={`${frameId}-artist`} className="mt-1 text-center text-[13px] text-[#9CA3AF]">未知艺术家</p>
          <audio src={streamURL} controls className="mt-5 w-full" />
        </div>
      )}
    </div>
  )
}
